// apps/admin/src/components/auth/MfaRecoveryPanel.tsx
//
// "Can't access your authenticator?" — the founder break-glass recovery screen
// from the approved MFA mockup (admin surface). GCIP allows only one
// authenticator per account (measured 2026-08-14), so there is no self-service
// spare code: recovery is the audited Admin-SDK reset using off-device recovery,
// logged as a break-glass event. This is NOT the App Check throttle path (that
// reuses AppCheckRecoveryBanner).

import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface MfaRecoveryPanelProps {
  onBack: () => void;
}

const STEPS: string[] = [
  'Use the audited Admin-SDK break-glass tools with your off-device recovery.',
  'Clear the enrolled factor for the account. The reset is logged as a break-glass event.',
  'Sign in with the password and enrol a fresh code on the new device.',
];

const MfaRecoveryPanel: React.FC<MfaRecoveryPanelProps> = ({ onBack }) => {
  return (
    <div className="w-full max-w-md p-8 rounded-2xl shadow-2xl bg-slate-800/70 backdrop-blur-xl border border-white/10 z-10">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 mb-4">
          <AlertTriangle size={30} className="text-slate-900" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Can&rsquo;t access your authenticator?</h2>
        <p className="text-slate-400 text-sm leading-relaxed">
          Lost or replaced your device? Your two-factor setup can be reset. There is no self-service
          second code by design.
        </p>
      </div>

      <div
        className="bg-amber-500/15 border border-amber-500/40 text-amber-100 p-4 rounded-lg mb-5 flex items-start"
        role="status"
      >
        <AlertTriangle size={18} className="mr-3 flex-shrink-0 mt-0.5 text-amber-300" />
        <span className="text-sm leading-relaxed">
          <b className="text-white">Founder / admin reset.</b> Reset the second factor with the
          audited admin tools, using your off-device recovery. The reset is logged as a break-glass
          event.
        </span>
      </div>

      <ol className="mb-6 space-y-0">
        {STEPS.map((step, index) => (
          <li
            key={step}
            className="flex items-start gap-3 py-2.5 text-sm text-slate-300 leading-relaxed border-b border-white/5 last:border-b-0"
          >
            <span className="flex-none w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-400 grid place-items-center text-xs font-bold">
              {index + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <button
        type="button"
        onClick={onBack}
        className="w-full bg-slate-800/60 border border-slate-600/60 text-slate-100 p-3 rounded-lg font-medium hover:bg-slate-700/70 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
      >
        Back to sign in
      </button>
    </div>
  );
};

export default MfaRecoveryPanel;
